import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";
import { ReactComponent as ChevronSolidIcon } from "assets/images/icons/icn_chevron_solid.svg";
import { ReactComponent as DeleteIcon } from "assets/images/icons/icn_table_cell_Unverified.svg";
import btn_add from "assets/images/icons/btn_add.svg";

import React, { useState } from "react";
import {
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  Paper,
  TableContainer,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { cloneDeep } from "lodash";

import { OutlineButton } from "components/buttons/OutlineButton";
import { SolidButton } from "components/buttons/SolidButton";
import IpInputField from "components/InputField/IpInputField";
import { isValidIpV4 } from "utils/network-validation";

const defaultAddItem = {
  open: true,
  ipAddress: "",
  subnet: "",
  description: "",
  ipTouched: false,
  subnetTouched: false,
};

const AddIpAddressModal = ({
  open,
  onClose,
  onApply,
}) => {
  const theme = useTheme();
  const [items, setItems] = useState([cloneDeep(defaultAddItem)]);

  const closeModal = () => {
    setItems([cloneDeep(defaultAddItem)]);
    onClose();
  };

  const updateItem = (index, key, value) => {
    setItems((prev) => {
      const next = cloneDeep(prev);
      next[index] = {
        ...next[index],
        [key]: value,
        ipTouched: key === "ipAddress" ? true : next[index].ipTouched,
        subnetTouched: key === "subnet" ? true : next[index].subnetTouched,
      };
      return next;
    });
  };

  const touchItem = (index, key) => {
    setItems((prev) => {
      const next = cloneDeep(prev);
      next[index] = {
        ...next[index],
        ipTouched: key === "ipAddress" ? true : next[index].ipTouched,
        subnetTouched: key === "subnet" ? true : next[index].subnetTouched,
      };
      return next;
    });
  };

  const toggleOpenItem = (index) => {
    setItems((prev) => {
      const next = cloneDeep(prev);
      next[index] = {
        ...next[index],
        open: !next[index].open,
      };
      return next;
    });
  };

  const addItem = () => {
    setItems((prev) => [...prev, cloneDeep(defaultAddItem)]);
  };

  const deleteItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const isSubnetValid = (value) => {
    if (value === "" || value === null || value === undefined) return false;
    if (!/^\d{1,2}$/.test(value)) return false;
    const numberValue = Number(value);
    return numberValue >= 0 && numberValue <= 32;
  };

  const isFormValid = () => {
    if (items.length === 0) return false;

    for (const item of items) {
      if (!isValidIpV4(item.ipAddress)) return false;
      if (!isSubnetValid(item.subnet)) return false;
    }

    return true;
  };

  const apply = () => {
    if (!isFormValid()) return;

    onApply(items.map((item) => ({
      ipAddress: item.ipAddress,
      subnet: item.subnet,
      description: item.description,
    })));
    closeModal();
  };

  if (!open) return null;

  const getTextFieldSx = (isValid = true) => ({
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "31px",
      backgroundColor: theme.palette.primary.white,
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.borderGreyPrimary
          : theme.palette.primary.redPrimary,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.mainHover
          : theme.palette.primary.redPrimary,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.main
          : theme.palette.primary.redPrimary,
      },
    },
  });
  const fieldStackSx = {
    minHeight: "53px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
  };
  const controlRowSx = {
    height: "32px",
    display: "flex",
    alignItems: "center",
  };
  const errorTextSx = {
    color: theme.palette.primary.redPrimary,
    fontSize: "12px",
    lineHeight: "18px",
    marginTop: "3px",
  };
  const addItemGridSx = {
    display: "grid",
    gridTemplateColumns: "26px 174px 300px 150px 150px",
    alignItems: "center",
  };
  const addItemHeaderGridSx = {
    ...addItemGridSx,
    cursor: "pointer",
    paddingTop: "20px",
    height: "40px",
  };
  const addButtonContainerStyle = {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: "20px",
    marginRight: "20px",
  };
  const addButtonStyle = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: "4px",
    padding: "4px 12px",
    minWidth: "120px",
    color: theme.palette.primary.main,
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          Add IP Address
        </Typography>
        <Button
          onClick={closeModal}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": { backgroundColor: theme.palette.primary.transparent },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          "&.MuiPaper-root": {
            boxShadow: "none",
            border: "none",
          },
        }}
        style={{
          maxHeight: "320px",
          marginLeft: "20px",
          width: "calc(100% - 40px)",
          overflowX: "auto",
          overflowY: items.length > 1 ? "auto" : "hidden",
        }}
      >
        <Box sx={{ minWidth: "800px" }}>
          {items.map((item, index) => {
            const isIpInvalid = !isValidIpV4(item.ipAddress);
            const isSubnetInvalid = !isSubnetValid(item.subnet);
            const showIpError = item.ipTouched && item.ipAddress && isIpInvalid;
            const showSubnetError = item.subnetTouched && isSubnetInvalid;

            return (
              <Box key={index}>
                <Box sx={addItemHeaderGridSx} onClick={() => toggleOpenItem(index)}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      transform: item.open ? "rotate(90deg)" : "rotate(0deg)",
                    }}
                  >
                    <ChevronSolidIcon />
                  </Box>
                  <Typography
                    sx={{
                      color: theme.palette.primary.greyPrimary,
                      fontSize: theme.typography.customFontSize[2],
                      fontWeight: theme.typography.customFontWeight[1],
                    }}
                  >
                    IP Address ({index + 1})
                  </Typography>
                  {index > 0 && (
                    <IconButton
                      sx={{ gridColumn: "5", justifySelf: "end" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteItem(index);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <Collapse in={item.open} timeout="auto" unmountOnExit>
                  <Box sx={{ ...addItemGridSx, marginTop: "20px" }}>
                    <Box />
                    <Box sx={fieldStackSx}>
                      <Box sx={controlRowSx}>
                        <Typography sx={{ color: theme.palette.primary.greyPrimary }}>
                          IP Address
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={fieldStackSx}>
                      <Box sx={controlRowSx}>
                        <Box
                          onBlur={() => touchItem(index, "ipAddress")}
                          sx={{
                            "& > div": {
                              backgroundColor: theme.palette.primary.white,
                              borderRadius: "8px",
                              margin: 0,
                            },
                          }}
                        >
                          <IpInputField
                            value={item.ipAddress}
                            onChange={(value) => updateItem(index, "ipAddress", value || "")}
                            error={item.ipTouched && isIpInvalid}
                          />
                        </Box>
                      </Box>
                      {showIpError && (
                        <Typography sx={errorTextSx}>
                          Invalid format
                        </Typography>
                      )}
                    </Box>
                    <Box sx={fieldStackSx}>
                      <Box sx={{ ...controlRowSx, justifyContent: "center" }}>
                        <Typography sx={{ color: theme.palette.primary.greyPrimary }}>Subnet</Typography>
                      </Box>
                    </Box>
                    <Box sx={fieldStackSx}>
                      <Box sx={controlRowSx}>
                        <TextField
                          value={item.subnet}
                          sx={{ width: "100px", ...getTextFieldSx(!(item.subnetTouched && isSubnetInvalid)) }}
                          inputProps={{ maxLength: 2 }}
                          onChange={(e) => updateItem(index, "subnet", e.target.value.replace(/\D/g, ""))}
                          onBlur={() => touchItem(index, "subnet")}
                        />
                      </Box>
                      {showSubnetError && (
                        <Typography sx={errorTextSx}>
                          0-32
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ ...addItemGridSx, marginTop: "16px", marginBottom: "10px" }}>
                    <Box />
                    <Typography sx={{ color: theme.palette.primary.greyPrimary }}>
                      Description
                    </Typography>
                    <TextField
                      value={item.description}
                      sx={getTextFieldSx(true)}
                      onChange={(e) => updateItem(index, "description", e.target.value)}
                    />
                    <Typography sx={{ color: theme.palette.primary.greyPrimary, textAlign: "center" }}>
                      (Optional)
                    </Typography>
                  </Box>
                </Collapse>

                <Divider sx={{ marginTop: "20px" }} />
              </Box>
            );
          })}

        </Box>
      </TableContainer>

      <Box display="flex" justifyContent="flex-end" marginTop="20px">
        <Box
          style={addButtonContainerStyle}
          onClick={addItem}
        >
          <Box style={addButtonStyle}>
            <img src={btn_add} alt="Add" />
            <span>Add New IP Address</span>
          </Box>
        </Box>
      </Box>

      <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
        <OutlineButton onClick={closeModal} sx={{ marginRight: "20px" }} width="100px" height="32px">
          Cancel
        </OutlineButton>
        <SolidButton onClick={apply} width="100px" height="32px" disabled={!isFormValid()}>
          Apply
        </SolidButton>
      </Box>
    </Box>
  );
};

export default AddIpAddressModal;
